import { useEffect, useEffectEvent, useRef } from 'react'
import { useQueries } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import queryClient from '@/lib/queryClient'
import { cartPaymentQueries } from '@/lib/services/cart-payments/cart-payments.query'
import { cartQueryKeys } from '@/lib/services/carts/carts.query'
import { useCheckoutAttemptStatus } from '@/lib/services/checkout/checkout.query'
import { paymentFinalizationDefaults } from './payment-finalization'
import {
  getCartFinalizationAttemptId,
  getPosPendingCardPayments,
} from './pos-finalization-contract'

export function usePosCartPaymentSynchronization(cart, { suspend = false } = {}) {
  const { orgId } = useParams({ strict: false })
  const cartId = Number(cart?.id ?? 0) || null
  const activeAttemptId = getCartFinalizationAttemptId(cart)
  const pendingPayments = getPosPendingCardPayments(cart)
  const handledTerminalStateSignaturesRef = useRef(new Set())
  const synchronizationIdentity = `${cartId ?? 'cart'}:${activeAttemptId ?? 'attempt'}:${pendingPayments
    .map((payment) => Number(payment?.id ?? 0))
    .join(',')}`

  const { data: attemptStatus, dataUpdatedAt: attemptStatusUpdatedAt } = useCheckoutAttemptStatus(
    activeAttemptId,
    {
      enabled: Boolean(cartId && activeAttemptId && !suspend),
      refetchInterval: paymentFinalizationDefaults.intervalMs,
    }
  )

  const paymentStatuses = useQueries({
    queries: pendingPayments.map((payment) => ({
      ...cartPaymentQueries.status({
        organizationId: orgId,
        cartId,
        paymentId: payment.id,
      }),
      enabled: Boolean(cartId && payment.id && !suspend),
      refetchInterval: paymentFinalizationDefaults.intervalMs,
    })),
  })
  const paymentStatusSnapshot = pendingPayments
    .map((payment, index) => {
      const result = paymentStatuses[index]

      return [
        Number(payment?.id ?? 0),
        result?.data?.state ?? 'processing',
        Number(result?.dataUpdatedAt ?? 0),
      ].join(':')
    })
    .join('|')

  useEffect(() => {
    handledTerminalStateSignaturesRef.current = new Set()
  }, [synchronizationIdentity])

  const invalidateCart = useEffectEvent((signature) => {
    if (!cartId || handledTerminalStateSignaturesRef.current.has(signature)) {
      return
    }

    handledTerminalStateSignaturesRef.current.add(signature)
    void Promise.all([
      queryClient.invalidateQueries({
        queryKey: cartQueryKeys.show({ organizationId: orgId, cartId }),
      }),
      queryClient.invalidateQueries({
        queryKey: cartQueryKeys.posShow({ organizationId: orgId, cartId }),
      }),
    ])
  })

  const synchronizeAttempt = useEffectEvent(() => {
    if (!attemptStatus || !attemptStatusUpdatedAt) {
      return
    }

    if (!['completed', 'payment_failed', 'expired', 'cancelled'].includes(attemptStatus.state)) {
      return
    }

    invalidateCart(`attempt:${activeAttemptId}:${attemptStatusUpdatedAt}`)
  })

  const synchronizePendingPayments = useEffectEvent(() => {
    paymentStatuses.forEach((result, index) => {
      const paymentId = Number(pendingPayments[index]?.id ?? 0)
      const paymentState = result?.data?.state
      const paymentStatusUpdatedAt = result?.dataUpdatedAt

      if (!paymentId || !paymentState || !paymentStatusUpdatedAt || paymentState === 'processing') {
        return
      }

      invalidateCart(`payment:${paymentId}:${paymentStatusUpdatedAt}`)
    })
  })

  useEffect(() => {
    if (suspend || !cartId) {
      return
    }

    if (activeAttemptId) {
      synchronizeAttempt()
      return
    }

    synchronizePendingPayments()
  }, [
    suspend,
    cartId,
    activeAttemptId,
    attemptStatus?.state,
    attemptStatusUpdatedAt,
    paymentStatusSnapshot,
  ])
}
